import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { logInternalError } from '../../lib/errors';
import { fetchAutocompleteSuggestions, fetchPlaceDetails, PlaceAddress } from '../../lib/placesAutocomplete';
import { getGoogleMapsApiKey } from '../../lib/stripeConfig';
import styles from './PlacesAutocompleteInput.module.scss';

interface PlacesAutocompleteInputProps {
  country: string;
  onSelect: (address: PlaceAddress) => void;
  inputRef?: React.Ref<HTMLInputElement>;
}

interface Suggestion {
  placeId: string;
  description: string;
}

function generateSessionToken() {
  return crypto.randomUUID();
}

export default function PlacesAutocompleteInput({ country, onSelect, inputRef }: PlacesAutocompleteInputProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const sessionTokenRef = useRef<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const containerRef = useRef<HTMLDivElement>(null);
  const apiKey = getGoogleMapsApiKey();

  // close dropdown on click outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && e.target instanceof Node && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setQuery(value);

      if (debounceRef.current) clearTimeout(debounceRef.current);

      if (!value.trim()) {
        setSuggestions([]);
        setOpen(false);
        return;
      }

      debounceRef.current = setTimeout(() => {
        if (!sessionTokenRef.current) {
          sessionTokenRef.current = generateSessionToken();
        }
        setLoading(true);
        void fetchAutocompleteSuggestions(value, country, apiKey, sessionTokenRef.current)
          .then((result) => {
            setUnavailable(result.unavailable);
            setSuggestions(result.suggestions);
            setOpen(result.suggestions.length > 0);
          })
          .catch((err) => {
            logInternalError(err, { tags: { component: 'PlacesAutocompleteInput' } });
            setUnavailable(true);
          })
          .finally(() => {
            setLoading(false);
          });
      }, 300);
    },
    [country, apiKey],
  );

  const handleSelect = useCallback(
    async (suggestion: Suggestion) => {
      setQuery(suggestion.description);
      setOpen(false);
      setSuggestions([]);

      try {
        const token = sessionTokenRef.current ?? generateSessionToken();
        const address = await fetchPlaceDetails(suggestion.placeId, apiKey, token);

        // reset session token after place details fetch (completes the billing session)
        sessionTokenRef.current = null;

        if (address) {
          onSelect(address);
        }
      } catch (err) {
        logInternalError(err instanceof Error ? err : new Error(String(err)), {
          tags: { component: 'PlacesAutocompleteInput', action: 'placeSelect' },
        });
        // user can still manually type their address in the stripe fields
        sessionTokenRef.current = null;
      }
    },
    [apiKey, onSelect],
  );

  // the address form below is always usable on its own, so rather than leave a search
  // box that can never return a result we take it away once google has told us no
  if (unavailable) return null;

  return (
    <div ref={containerRef} className={styles.container}>
      <div className={styles.label}>{t('search_address')}</div>
      <input
        ref={inputRef}
        type="text"
        className={styles.input}
        value={query}
        onChange={handleInputChange}
        placeholder={t('search_address')}
        autoComplete="off"
      />
      {open && suggestions.length > 0 && (
        <ul className={styles.dropdown}>
          {suggestions.map((s) => (
            <li key={s.placeId} className={styles.suggestion} onMouseDown={() => void handleSelect(s)}>
              {s.description}
            </li>
          ))}
        </ul>
      )}
      {loading && <div className={styles.loading} />}
    </div>
  );
}
